import {
  COST_BUDGET_COLUMN_ACCESSORS,
  COST_BUDGET_RFQ_COLUMN_ACCESSORS,
  getMyOfferTableColumns,
  MY_OFFER_GCPR_COLUMN_ACCESSORS,
  MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
  MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
  PR_COLUMN_ACCESSORS,
  PR_LINE_ITEM_COLUMN_ACCESSORS,
} from '@/features/offers/constants/offerListColumns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

// UI
import { AiFillCloseSquare } from 'react-icons/ai'
import { FaClone } from 'react-icons/fa'
import { GrPowerCycle } from 'react-icons/gr'
import { IoMdCreate } from 'react-icons/io'
import { MdPersonAddAlt1 } from 'react-icons/md'

// Components
import FullTable, { tableInitialState } from '@/components/Table/FullTable/FullTable'

// Hooks
import useOfferAction from '../../hooks/useOfferAction'

// Constants
import { UserEditRight, UserRole } from '@/features/users/constants'
import { MaintenanceSummaryStatus, OfferControlStatus, OfferQuotationStatus, OfferStage } from '../../constants'

// Helpers
import { createColumnHelper } from '@tanstack/react-table'
import { getStage } from '../../helpers'

// Slices
import { showAdminEditOfferModal, showCloneOfferModal, showReviseOfferModal } from '@/stores/offers/offersSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

// Components
import TableProfileMenu from '@/components/TableProfileMenu'
import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import { authSelector } from '@/stores/auth/authSlice'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import useOfferList from '../../hooks/useOfferList'
import { showCloseOfferModal } from '../../slices/offerModal.slice'
import MyOfferActionMenuButton from './MyOfferActionMenuButton'
import MyOfferPageHeader from './MyOfferPageHeader'
const MyOfferTable = ({ userTableProfile, URLSelectedProfileId = '', columnData = {} }) => {
  const initialTableState = useMemo(() => {
    try {
      if (!userTableProfile) {
        throw new Error('1')
      }

      if (URLSelectedProfileId !== '') {
        const selectedProfile = userTableProfile.profiles.find((profile) => profile._id === URLSelectedProfileId)

        if (!selectedProfile?.tableDetails) {
          throw new Error('2')
        }
        return selectedProfile.tableDetails
      }

      if (!userTableProfile?.defaultProfile?.tableDetails) {
        throw new Error('3')
      }
      return userTableProfile?.defaultProfile?.tableDetails
    } catch (err) {
      console.log(err)
      return tableInitialState
    }
  }, [URLSelectedProfileId, userTableProfile])

  const initialTableDisplayLevel = useMemo(() => {
    try {
      if (!userTableProfile) {
        throw new Error('1')
      }
      if (URLSelectedProfileId !== '') {
        const selectedProfile = userTableProfile.profiles.find((profile) => profile._id === URLSelectedProfileId)

        if (!selectedProfile?.tableDetails) {
          throw new Error('2')
        }
        return selectedProfile.displayLevel
      }

      if (!userTableProfile?.defaultProfile?.tableDetails) {
        throw new Error('3')
      }
      return userTableProfile?.defaultProfile?.displayLevel
    } catch (err) {
      console.log(err)
      return MyOfferDisplayLevel.OFFER
    }
  }, [URLSelectedProfileId, userTableProfile])

  const [tableState, setTableState] = useState(initialTableState)
  const [displayLevel, setDisplayLevel] = useState(initialTableDisplayLevel)

  const query = convertTableStateToQuery(tableState)
  const columnHelper = createColumnHelper()
  const { user: currentUser } = useSelector(authSelector)

  const {
    useCreateOfferBooking,
    useNoticeCurrentApprover,
    useActivateOffer,
    useAddCustomerToOffer,
    useToggleOfferRenewalLost,
    useUncloseOffer,
  } = useOfferAction()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { useFetchMyOffers } = useOfferList()

  const createOfferBooking = useCreateOfferBooking()
  const noticeCurrentApprover = useNoticeCurrentApprover()
  const activateOffer = useActivateOffer()
  const addCustomerToOffer = useAddCustomerToOffer()
  const toggleOfferRenewalLost = useToggleOfferRenewalLost()
  const uncloseOffer = useUncloseOffer()

  const { data: offersResponse, isLoading: isLoadingOffer, isRefetching } = useFetchMyOffers(query, displayLevel)

  const handleOfferActionButtonClicked = useCallback(
    async (e, { type, offerId, ...rest }) => {
      e.stopPropagation()
      try {
        if (!type) {
          throw new Error(`Unexpected Error, Invalid offer action type ! type: ${type}`)
        }

        if (!offerId) {
          throw new Error(`Unexpected Error, Invalid offer id ! id: ${offerId}`)
        }

        switch (type) {
          case 'cloneOffer':
            dispatch(
              showCloneOfferModal({
                offerId: offerId,
                salesId: rest.salesId,
                isRenew: false,
              })
            )
            break
          case 'addCustomerToOffer': {
            dispatch(showLoadingOverlay())
            const addCustomerResponse = await addCustomerToOffer.mutateAsync({ offerId })
            if (addCustomerResponse?._id) {
              window.open(`/offers/${addCustomerResponse?._id}`, '_blank')
            }

            break
          }
          case 'closeOffer':
            dispatch(
              showCloseOfferModal({
                offerId,
                type: 'offer',
              })
            )
            break
          case 'reviseOffer':
            console.log('reviseOffer', rest)
            dispatch(
              showReviseOfferModal({
                offerId: offerId,
                ...rest,
              })
            )
            break
          case 'createBooking':
            dispatch(showLoadingOverlay())

            await createOfferBooking.mutateAsync({ offerId })
            navigate(`/offers/${offerId}`)
            break
          case 'activateOffer': {
            dispatch(showLoadingOverlay())

            const res = await activateOffer.mutateAsync({ offerId })

            const newOfferId = res?._id
            if (newOfferId && newOfferId !== '') {
              navigate(`/offers/${newOfferId}`)
            }
            break
          }
          case 'noticeCurrentApprover':
            dispatch(showLoadingOverlay())
            await noticeCurrentApprover.mutateAsync({ offerId })
            break
          case 'adminEditOffer':
            dispatch(
              showAdminEditOfferModal({
                offerId: offerId,
              })
            )
            break
          case 'renewOffer':
            dispatch(
              showCloneOfferModal({
                offerId: offerId,
                salesId: rest.salesId,
                subSystemIndex: rest?.subSystemIndex,
                isRenew: true,
              })
            )
            break
          case 'renewalLost':
            dispatch(
              showCloseOfferModal({
                show: true,
                offerId: offerId,
                type: 'maintenance',
              })
            )
            break
          case 'undoRenewalLost':
            await toggleOfferRenewalLost.mutateAsync({ offerId })
            break
          case 'uncloseOffer':
            await uncloseOffer.mutateAsync({ offerId })
            break

          default:
            throw new Error(`Unexpected Error, Invalid offer action type ! type: ${type}`)
        }
      } catch (error) {
        console.error(error)
        toast.error('Failed to perform offer action.')
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, addCustomerToOffer, createOfferBooking, activateOffer, noticeCurrentApprover, navigate]
  )

  const getOfferButtons = useCallback(
    (offer) => {
      const buttons = [
        {
          title: 'Clone',
          icon: <FaClone size={18} />,
          onClick: (e) =>
            handleOfferActionButtonClicked(e, { type: 'cloneOffer', offerId: offer._id, salesId: offer?.salesId }),
        },
      ]

      if (getStage(offer.status) === OfferStage.QUOTATION) {
        buttons.push({
          title: 'Add Customer',
          icon: <MdPersonAddAlt1 size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'addCustomerToOffer', offerId: offer._id }),
        })
      }

      if (
        offer.isClosed === 'no' &&
        (currentUser.role === UserRole.ADMIN || getStage(offer.status) === OfferStage.QUOTATION)
      ) {
        buttons.push({
          title: 'Close',
          icon: <AiFillCloseSquare size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'closeOffer', offerId: offer._id }),
        })
      }

      if (
        offer.isClosed === 'yes' &&
        (currentUser.role === UserRole.ADMIN || getStage(offer.status) === OfferStage.QUOTATION)
      ) {
        buttons.push({
          title: 'Unclose',
          icon: <AiFillCloseSquare size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'uncloseOffer', offerId: offer._id }),
        })
      }

      if (offer.status === OfferQuotationStatus.QUOTATION_COMPLETED) {
        buttons.push({
          title: 'Revise',
          icon: <GrPowerCycle size={18} />,
          onClick: (e) =>
            handleOfferActionButtonClicked(e, {
              type: 'reviseOffer',
              offerId: offer._id,
              projectName: offer.projectName,
            }),
        })
        buttons.push({
          title: 'Create Booking',
          icon: <IoMdCreate size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'createBooking', offerId: offer._id }),
        })
      }

      if (offer.status === OfferControlStatus.SUPERSEDED) {
        buttons.push({
          title: 'Activate',
          icon: <IoMdCreate size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'activateOffer', offerId: offer._id }),
        })
      }

      if (offer?.nextApprover !== '') {
        buttons.push({
          title: 'Notice Current Approver',
          icon: <IoMdCreate size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'noticeCurrentApprover', offerId: offer._id }),
        })
      }
      if (currentUser.role === UserRole.ADMIN || currentUser?.editRights.includes(UserEditRight.WORKFLOW)) {
        buttons.push({
          title: 'Admin Edit',
          icon: <IoMdCreate size={18} />,
          onClick: (e) => handleOfferActionButtonClicked(e, { type: 'adminEditOffer', offerId: offer._id }),
        })
      }

      if (
        offer?.maintenanceContractStatus &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.NOT_COMPLETED &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.RENEWAL_LOST &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.RENEWED &&
        offer?.subSystemIndex !== ''
      ) {
        buttons.push({
          title: 'Renew',
          icon: <FaClone size={18} />,
          onClick: (e) =>
            handleOfferActionButtonClicked(e, {
              type: 'renewOffer',
              offerId: offer._id,
              salesId: offer?.salesId,
              subSystemIndex: offer?.subSystemIndex,
            }),
        })
      }
      if (
        offer?.maintenanceContractStatus &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.NOT_COMPLETED &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.RENEWAL_LOST &&
        offer?.maintenanceContractStatus !== MaintenanceSummaryStatus.RENEWED
      ) {
        buttons.push({
          title: 'Renewal Lost',
          icon: <FaClone size={18} />,
          onClick: (e) =>
            handleOfferActionButtonClicked(e, {
              type: 'renewalLost',
              offerId: offer._id,
            }),
        })
      }

      if (
        offer?.maintenanceContractStatus &&
        offer?.maintenanceContractStatus === MaintenanceSummaryStatus.RENEWAL_LOST
      ) {
        buttons.push({
          title: 'Undo Renewal Lost',
          icon: <FaClone size={18} />,
          onClick: (e) =>
            handleOfferActionButtonClicked(e, {
              type: 'undoRenewalLost',
              offerId: offer._id,
            }),
        })
      }
      return buttons
    },
    [handleOfferActionButtonClicked, currentUser]
  )

  const allColumns = useMemo(() => {
    const defaultColumns = getMyOfferTableColumns(columnData, displayLevel)
    return [
      columnHelper.display({
        id: 'action',
        size: 30,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return <MyOfferActionMenuButton buttons={getOfferButtons(original)} />
        },
      }),
      ...defaultColumns,
    ]
  }, [columnHelper, getOfferButtons, columnData, displayLevel])

  const handleGotoOffer = useCallback((row) => {
    if (!row?.original?._id) {
      return
    }
    window.open(`${window.location.origin}/offers/${row?.original?._id}`)
  }, [])

  useEffect(() => {
    switch (displayLevel) {
      case MyOfferDisplayLevel.OFFER: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.SUB_SYSTEM: {
        const accessorsToHide = [
          ...COST_BUDGET_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.COST_BUDGET: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.COST_BUDGET_RFQ: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.GCPR: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.GCPR_LINE_ITEM: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...PR_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.PR: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
          ...PR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }

      case MyOfferDisplayLevel.PR_LINE_ITEM: {
        const accessorsToHide = [
          ...MY_OFFER_SUB_SYSTEM_COLUMN_ACCESSORS,
          ...COST_BUDGET_RFQ_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_COLUMN_ACCESSORS,
          ...MY_OFFER_GCPR_LINE_ITEM_COLUMN_ACCESSORS,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }
    }
  }, [displayLevel])

  if (isLoadingOffer) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    )
  }
  return (
    <>
      <Flex direction={'column'} gap="2">
        <MyOfferPageHeader
          count={offersResponse?.count ?? 0}
          displayLevel={displayLevel}
          query={query}
          tableState={tableState}
          userTableProfile={userTableProfile}
          setTableState={setTableState}
          setTableDisplayLevel={setDisplayLevel}
          URLSelectedProfileId={URLSelectedProfileId}
        />
      </Flex>
      <Box py={5} w="full" h="90%" overflowY="scroll">
        <FullTable
          data={offersResponse?.data ?? []}
          rowCount={offersResponse?.count ?? 0}
          columnDef={allColumns}
          state={tableState}
          setState={setTableState}
          handleRowClick={handleGotoOffer}
          showColumnGroup={true}
          displayLevel={displayLevel}
          isLoading={isRefetching}
        />
      </Box>
    </>
  )
}

export default MyOfferTable
